import React, { useContext } from 'react';
import {
  bool,
  number,
  string
} from 'prop-types';
import { TextField, InputAdornment } from '@one-thd/sui-atomic-components';
import { getError } from '../../helpers/errorHandlingUtil';
import { CalculatorContext } from './CalculatorProvider';

// Regex is loose so that users are free to type out what they need without too many restrictions
// For more info on this Regex, try it out at https://ihateregex.io/playground
const looseNumberRegex = /^[0-9\b]{0,5}?([.]([0-9\b]{1,2})?)?$/;

export const CalculatorInputElement = ({
  endAdornment,
  index,
  isError,
  labelName,
  propName,
  propValue
}) => {
  const { saveToLocalStorageFunc, setCalculate } = useContext(CalculatorContext);
  const lowerCasePropName = propName.toLowerCase();

  const endAdornmentProp = endAdornment
    ? <InputAdornment position="end">{endAdornment}</InputAdornment>
    : undefined;

  const pattern = propName === 'Description'
    ? ''
    : looseNumberRegex;
  let errorMessage = '';
  if (isError) {
    if (propName === 'Description') {
      errorMessage = 'Name is already in use, please use a different name';
    } else if (propValue === '') {
      errorMessage = 'Please enter all required fields';
    } else {
      errorMessage = 'Please enter a number above 0';
    }
  }

  return (
    <div className="sui-mr-3">
      <TextField
        fullWidth
        label={`${labelName || propName}:`}
        onChange={(event) => {
          const { value = '' } = event.target;
          if (value === '' || looseNumberRegex.test(value) || propName === 'Description') {
            setCalculate((prevState) => {
              const { areas, areaErrors } = prevState;
              // get updated areas
              const newAreas = JSON.parse(JSON.stringify(areas));
              const newArea = newAreas[index];
              if (lowerCasePropName === 'square footage') {
                newArea.squareFootage = {
                  value,
                  changed: Number(prevState.areas[index].squareFootage.value) !== Number(value)
                };
              } else if (lowerCasePropName === 'description') {
                newArea.name = value;
              } else {
                newArea[lowerCasePropName] = value;
              }
              // get updated area errors
              const errorCal = getError({
                name: lowerCasePropName, index, prevAreas: areas, newAreas
              });
              const newAreaErrors = JSON.parse(JSON.stringify(areaErrors));
              if (lowerCasePropName === 'square footage') {
                newAreaErrors[index].squareFootage = errorCal;
              } else if (lowerCasePropName === 'description') {
                newAreas.forEach((element, dupeIndex) => {
                  // if current field has errors, add errors to all duplicate names
                  if (errorCal && element.name === newArea.name) {
                    newAreaErrors[dupeIndex].name = errorCal;
                  } else if (!errorCal) {
                    // if current field doesn't have errors, remove errors from other no-longer-duplicate names
                    if (dupeIndex === index
                      || (element.name === areas[index].name
                        && newAreas.filter((innerArea) => innerArea.name === areas[index].name)).length <= 1) {
                      newAreaErrors[dupeIndex].name = errorCal;
                    }
                  }
                });
              } else {
                newAreaErrors[index][lowerCasePropName] = errorCal;
              }

              return {
                ...prevState,
                areas: newAreas,
                areaErrors: newAreaErrors,
                changesAwaitingLocalStorage: true
              };
            });
          }
        }}
        type="string"
        pattern={pattern}
        name={lowerCasePropName}
        value={propValue}
        helpMessage={propName === 'Depth' ? '3 in. recommended' : null}
        status={errorMessage ? 'error' : null}
        statusMessage={errorMessage}
        InputProps={{
          onBlur: () => saveToLocalStorageFunc(),
          endAdornment: endAdornmentProp
        }}
      />
    </div>
  );
};

CalculatorInputElement.propTypes = {
  endAdornment: string,
  index: number.isRequired,
  isError: bool.isRequired,
  labelName: string,
  propName: string.isRequired,
  propValue: string.isRequired
};

CalculatorInputElement.defaultProps = {
  endAdornment: '',
  labelName: ''
};
